import React, { useCallback, useEffect, useState } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import {
  useGetLocationsInventoryVolumesQuery,
  useGetToLocationsQuery,
} from 'feature/inventory-management/reconsignments/reconsignmentsEndpoints';
import { ErrorOutline } from '@mui/icons-material';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { DataLoadingCard } from 'common';
import {
  CONSIGNMENT_STATUS_OPTIONS,
  INVENTORY_STATUS_OPTIONS,
} from 'feature/inventory-management/reconsignments/utils/constant';
import { complement, isNil, pickBy } from 'ramda';
import LocationDetailsDrawer from '../LocationDetailsDrawer';
import toLocationColumns from './ToLocationsColumns';
import ToLocationDataGrid from './ToLocationsDataGrid';
import SelectProductCard from '../../OneToManyWizard/SelectProductCard';

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton variant="dataTable" />
    <GridToolbarDensitySelector variant="dataTable" />
  </GridToolbarContainer>
);

const InfoSection = ({ title, value, error }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography color="textSecondary" variant="caption">
      {title}
    </Typography>
    <Typography
      sx={{
        color: error ? 'error.main' : 'inherit',
        fontWeight: 600,
      }}
      variant="caption"
    >
      {value}
    </Typography>
  </Box>
);

InfoSection.propTypes = {
  error: PropTypes.bool,
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

InfoSection.defaultProps = {
  error: false,
  title: '',
  value: '',
};

const ActionButton = ({ label, iconSize }) => (
  <Button
    endIcon={<CloseRoundedIcon sx={{ fontSize: iconSize, ml: 0.6 }} />}
    size="small"
    sx={{ minWidth: 'auto', px: 1, py: 0.5, textTransform: 'none' }}
    variant="outlined"
  >
    {label}
  </Button>
);

ActionButton.propTypes = {
  iconSize: PropTypes.number,
  label: PropTypes.string.isRequired,
};

ActionButton.defaultProps = {
  iconSize: 16,
};
const WarningBanner = ({ show }) =>
  show ? (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'warning.light',
        borderRadius: 1,
        color: 'warning.dark',
        display: 'flex',
        mb: 2,
        p: 2,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'yellow',
          borderRadius: '50%',
          display: 'flex',
          flexShrink: 0,
          height: 40,
          justifyContent: 'center',
          width: 40,
        }}
      >
        <ErrorOutline
          sx={{
            color: 'orange',
            fontSize: 20,
          }}
        />
      </Box>
      <Typography>
        You have exceeded the Total Transfer Volume available. Please reduce the Transfer Volume or
        modify your filters to continue.
      </Typography>
    </Box>
  ) : null;
WarningBanner.propTypes = {
  show: PropTypes.bool.isRequired,
};

export default function ToLocationGrid({
  reconsignmentState,
  selectedOrigin,
  setLocationsInventoryVolumes,
  setSelectedOrigin,
  setReconsignmentState,
  setTransferVolume,
  setToLocations,
  toLocations,
  isVolumeExceeded,
  setIsVolumeExceeded,
  formattedTransferDate,
  gridKey,
  setGridKey,
  showOnlyLocationsWithVolume,
}) {
  const apiRef = useGridApiRef();
  const columns = toLocationColumns;
  const [totalTransferVolume, setTotalTransferVolume] = useState(0);
  const [maxAllowedVolume, setMaxAllowedVolume] = useState(0);

  const fromLocationsPams = {
    FromLocation: R.prop('locationId', reconsignmentState?.fromLocation),
    LocationId: R.prop('locationId', reconsignmentState?.fromLocation),
    ProductId: R.pathOr('', ['product', 'productId'], reconsignmentState),
    inventoryOwner: {
      companyCustomerIdName: R.path(['inventoryOwner', 'customerIdName'], reconsignmentState),
      dataCompanyId: R.path(['inventoryOwner', 'dataCompanyId'], reconsignmentState),
    },
  };

  const {
    data: fromLocations,
    isLoading,
    isSuccess,
  } = useGetToLocationsQuery({
    params: fromLocationsPams,
  });
  const rvpVal = R.prop('rvp', reconsignmentState);
  const rawParams = {
    ...fromLocationsPams,
    ConsignmentStatus:
      R.toUpper(R.prop('consignmentStatus', reconsignmentState) || '') === 'ALL'
        ? undefined
        : R.prop('consignmentStatus', reconsignmentState),
    InventoryStatus:
      R.toUpper(R.prop('inventoryStatus', reconsignmentState) || '') === 'ALL'
        ? undefined
        : R.prop('inventoryStatus', reconsignmentState),
    OriginIds: R.pluck('originId', R.prop('origin', reconsignmentState) || []),
    RVP:
      typeof rvpVal === 'string' ? (rvpVal.toUpperCase() === 'ANY' ? undefined : rvpVal) : rvpVal,
  };

  const locationsInventoryVolumesParams = pickBy(complement(isNil), rawParams);
  const { data: locationsInventoryVolumes } = useGetLocationsInventoryVolumesQuery({
    params: locationsInventoryVolumesParams,
  });

  useEffect(() => {
    if (locationsInventoryVolumes) {
      const availableVol = Number(locationsInventoryVolumes.availableVolume) || 0;
      const inTransitVol = Number(locationsInventoryVolumes.inTransitVolume) || 0;
      const totalAvailableVolume = availableVol + inTransitVol;
      setMaxAllowedVolume(totalAvailableVolume);
      setLocationsInventoryVolumes(locationsInventoryVolumes);

      // Check if current total volume exceeds the new max allowed volume
      if (totalTransferVolume > totalAvailableVolume) {
        setIsVolumeExceeded(true);
      }
    }
  }, [
    locationsInventoryVolumes,
    setIsVolumeExceeded,
    setLocationsInventoryVolumes,
    totalTransferVolume,
  ]);

  const [selecedLocationDetails, setSelectedLocationDetails] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedLocationDetails(null);
  };

  const handleSelectOrigins = useCallback(() => {
    const fromLoc = reconsignmentState.fromLocation;
    setSelectedLocationDetails(
      R.mergeRight(fromLoc, {
        inventoryOwner: R.path(['inventoryOwner', 'customerNameShort'], reconsignmentState),
        product: R.pathOr('', ['product', 'productName'], reconsignmentState),
        rvp: R.defaultTo('ANY', R.prop('rvp', reconsignmentState)),
        totalVolume: R.propOr(null, 'totalVolume', fromLoc),
      })
    );
    setDrawerOpen(true);
  }, [reconsignmentState]);

  useEffect(() => {
    const isExceeded = totalTransferVolume > maxAllowedVolume;
    setIsVolumeExceeded(isExceeded);
    setTransferVolume(totalTransferVolume);
  }, [totalTransferVolume, maxAllowedVolume, setTransferVolume, setIsVolumeExceeded]);

  const consignmentStatusCode = R.pipe(
    R.propOr('', 'consignmentStatus'),
    R.toUpper
  )(reconsignmentState);
  const consignmentStatuLabel = R.pipe(
    R.find(R.propEq('code', consignmentStatusCode)),
    R.propOr(consignmentStatusCode, 'label')
  )(CONSIGNMENT_STATUS_OPTIONS);

  const inventoryStatusCode = R.pipe(
    R.propOr('', 'inventoryStatus'),
    R.toUpper
  )(reconsignmentState);
  const inventoryStatusLabel = R.pipe(
    R.find(R.propEq('code', inventoryStatusCode)),
    R.propOr(inventoryStatusCode, 'label')
  )(INVENTORY_STATUS_OPTIONS);

  return (
    <Box sx={{ p: 3, pt: 8 }}>
      <Box
        alignItems="start"
        columnGap={4}
        display="grid"
        gridTemplateColumns="1fr 1fr"
        mb={3}
        rowGap={1}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography
            sx={{
              flexWrap: 'nowrap',
              fontFamily: 'Overpass',
              fontSize: '24px',
              fontWeight: 400,
              lineHeight: 'normal',
              whiteSpace: 'nowrap',
            }}
            variant="h5"
          >
            Input Volume for To Location(s)
          </Typography>
          <Typography
            color="textSecondary"
            sx={{
              flexWrap: 'nowrap',
              fontFamily: 'Gothic A1',
              fontSize: '12.5px',
              fontWeight: 600,
              letterSpacing: '0.4px',
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
            variant="body2"
          >
            Input volume for the locations you want to send volume
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              maxWidth: '100%',
              mt: 0.5,
              overflow: 'visible',
              whiteSpace: 'normal',
            }}
          >
            {selectedOrigin?.map(origin => (
              <Chip
                key={R.prop('originId', origin)}
                label={
                  <>
                    <strong>ORIGIN:</strong> {R.prop('originName', origin)}
                  </>
                }
                sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
                variant="outlined"
              />
            ))}
            {R.prop('rvp', reconsignmentState) && (
              <Chip
                label={
                  <>
                    <strong>RVP:</strong>{' '}
                    {R.is(String, R.prop('rvp', reconsignmentState))
                      ? R.toUpper(R.prop('rvp', reconsignmentState))
                      : R.prop('rvp', reconsignmentState)}
                  </>
                }
                sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
                variant="outlined"
              />
            )}

            <Chip
              label={
                <>
                  <strong>INVENTORY STATUS:</strong> {(inventoryStatusLabel || '').toUpperCase()}
                </>
              }
              sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
              variant="outlined"
            />
            <Chip
              label={
                <>
                  <strong>CONSIGNMENT STATUS:</strong> {(consignmentStatuLabel || '').toUpperCase()}
                </>
              }
              sx={{ borderColor: 'secondary.main', borderRadius: 25, textTransform: 'uppercase' }}
              variant="outlined"
            />
          </Box>
        </Box>
        <Box
          alignItems="center"
          display="flex"
          gap={6}
          sx={{
            '& > *': {
              flexShrink: 0,
            },
            flexWrap: 'nowrap',
            justifyContent: 'flex-end',
            whiteSpace: 'nowrap',
          }}
        >
          <InfoSection
            title="From Location"
            value={`${reconsignmentState?.fromLocation?.locationIdn} ${reconsignmentState?.locationName}`}
          />
          <InfoSection
            title="Product ID"
            value={R.pipe(
              R.pathOr('', ['product', 'productName']),
              R.split(' '),
              R.head,
              R.defaultTo('')
            )(reconsignmentState)}
          />
          <InfoSection title="Transfer Date" value={formattedTransferDate} />
          <InfoSection
            title="Available Volume"
            value={`${formatNumberWithoutDecimals(
              locationsInventoryVolumes?.availableVolume
            )} bbls`}
          />
          <InfoSection
            title="Intransit Volume"
            value={`${formatNumberWithoutDecimals(
              locationsInventoryVolumes?.inTransitVolume
            )} bbls`}
          />
          <InfoSection
            error={isVolumeExceeded}
            title="Total Transfer Volume"
            value={`${formatNumberWithoutDecimals(totalTransferVolume)} bbls`}
          />
          <Button onClick={handleSelectOrigins} size="small" variant="outlined">
            Select Origin
          </Button>
        </Box>
      </Box>

      <WarningBanner show={isVolumeExceeded} />

      {isLoading ? (
        <DataLoadingCard type="data" />
      ) : isSuccess ? (
        <ToLocationDataGrid
          apiRef={apiRef}
          columns={columns}
          customToolbar={CustomToolbar}
          getRowId={row => row.locationId}
          gridKey={gridKey}
          isVolumeExceeded={isVolumeExceeded}
          key={gridKey}
          loading={isLoading}
          maxAllowedVolume={maxAllowedVolume}
          rows={fromLocations}
          setGridKey={setGridKey}
          setToLocations={setToLocations}
          setTotalTransferVolume={setTotalTransferVolume}
          showOnlyLocationsWithVolume={showOnlyLocationsWithVolume}
          toLocations={toLocations}
        />
      ) : (
        <SelectProductCard
          subtitle="Return to Step 1 and select a different product or location for your reconsignment."
          title="There are no locations that can accept this product from the selected From Location."
        />
      )}
      <LocationDetailsDrawer
        location={selecedLocationDetails}
        onClose={handleCloseDrawer}
        open={drawerOpen}
        reconsignmentState={reconsignmentState}
        selectedOrigin={selectedOrigin}
        setReconsignmentState={setReconsignmentState}
        setSelectedOrigin={setSelectedOrigin}
        showCheckboxes
        showSubmitButton
      />
    </Box>
  );
}

ToLocationGrid.propTypes = {
  formattedTransferDate: PropTypes.string.isRequired,
  gridKey: PropTypes.number.isRequired,
  isVolumeExceeded: PropTypes.bool.isRequired,
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.string,
    inventoryOwner: PropTypes.shape({}),
    locationName: PropTypes.string,
    product: PropTypes.shape({}),
  }).isRequired,
  selectedOrigin: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setGridKey: PropTypes.func.isRequired,
  setIsVolumeExceeded: PropTypes.func.isRequired,
  setLocationsInventoryVolumes: PropTypes.func.isRequired,
  setReconsignmentState: PropTypes.func.isRequired,
  setSelectedOrigin: PropTypes.func.isRequired,
  setToLocations: PropTypes.func.isRequired,
  setTransferVolume: PropTypes.func.isRequired,
  showOnlyLocationsWithVolume: PropTypes.bool.isRequired,
  toLocations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
